import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import DateRangePicker from '@/components/DateRangePicker';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { dateRangesDashboard, today } from '@/constants/datePreset';
import { TimeChoices } from '@/constants/enum';
import { calculateTimeRange, getDateRangeWithPreviousPeriod } from '@/helpers';
import { useGetCrispDataQuery, useGetOrderAnalyticsMutation } from '@/redux/query';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  ChoiceList,
  Divider,
  Icon,
  InlineGrid,
  InlineStack,
  Popover,
  useBreakpoints,
} from '@shopify/polaris';
import { ArrowRightIcon, LocationIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomColorGrowth, FullWidthDiv, PointerDiv } from '../../styled';

interface Props {
  listLocationName: {
    value: string;
    label: string;
  }[];
  hasEnableMethods?: {
    isEnablePickup: boolean;
    isEnableDelivery: boolean;
    isEnableShipping: boolean;
  };
}

export const SaleInformation = ({ listLocationName, hasEnableMethods }: Props) => {
  const { t } = useTranslation('dashboard');
  const { lgUp } = useBreakpoints();

  const initialTimeRange = getDateRangeWithPreviousPeriod({
    start: dateRangesDashboard[2].period.since,
    end: dateRangesDashboard[2].period.until,
  });

  const [popOverActive, setPopOverActive] = useState({
    isTime: false,
    isLocation: false,
  });

  const [saleReport, setSaleReport] = useState({
    locationLabel: 'All locations',
    locationSelected: [''],
    startTime: initialTimeRange.startTime,
    endTime: initialTimeRange.endTime,
    previousPeriodStartTime: initialTimeRange.previousPeriodStartTime,
    previousPeriodEndTime: initialTimeRange.previousPeriodEndTime,
  });

  const [getOrderAnalytics, { data: orderAnalyticData }] = useGetOrderAnalyticsMutation();

  const { data: crispData } = useGetCrispDataQuery({ shop: config.shop });

  const locationChoices = useMemo(() => [{ label: 'All locations', value: '' }, ...listLocationName], [listLocationName]);

  const toggleLocationPopoverActive = useCallback(
    () => setPopOverActive((popoverActive) => ({ ...popoverActive, isLocation: !popoverActive.isLocation })),
    [],
  );

  const handleChangeLocation = useCallback(
    (value: string[]) => {
      const selectedChoice = locationChoices.find((choice) => choice.value === value[0]);
      setSaleReport((prev) => ({
        ...prev,
        locationSelected: value,
        locationLabel: selectedChoice?.label || saleReport.locationLabel,
      }));
      toggleLocationPopoverActive();
    },
    [locationChoices, saleReport.locationLabel, toggleLocationPopoverActive],
  );

  const locationActivator = (
    <Button onClick={toggleLocationPopoverActive} icon={LocationIcon} disclosure>
      {saleReport.locationLabel}
    </Button>
  );

  const { isEnablePickup, isEnableDelivery, isEnableShipping } = hasEnableMethods!;

  const orderOverViewData = useMemo(() => {
    const rawData = [
      {
        key: 'shipping',
        title: t('sale.overViewDetail.shipping'),
        scheduleOrders: orderAnalyticData?.data.ordersByType.shipping.count || 0,
        unfulfilledOrders: orderAnalyticData?.data.ordersByType.shipping.unfulfilled || 0,
        growth: orderAnalyticData?.data.ordersByType.shipping.growthRate || 0,
        isEnabled: isEnableShipping,
      },
      {
        key: 'delivery',
        title: t('sale.overViewDetail.delivery'),
        scheduleOrders: orderAnalyticData?.data.ordersByType.delivery.count || 0,
        unfulfilledOrders: orderAnalyticData?.data.ordersByType.delivery.unfulfilled || 0,
        growth: orderAnalyticData?.data.ordersByType.delivery.growthRate || 0,
        isEnabled: isEnableDelivery,
      },
      {
        key: 'pickup',
        title: t('sale.overViewDetail.pickup'),
        scheduleOrders: orderAnalyticData?.data.ordersByType.pickup.count || 0,
        unfulfilledOrders: orderAnalyticData?.data.ordersByType.pickup.unfulfilled || 0,
        growth: orderAnalyticData?.data.ordersByType.pickup.growthRate || 0,
        isEnabled: isEnablePickup,
      },
    ];

    const hasEnabled = rawData.some((item) => item.isEnabled);
    return hasEnabled ? rawData.filter((item) => item.isEnabled) : rawData;
  }, [t, orderAnalyticData, isEnableShipping, isEnableDelivery, isEnablePickup]);

  const renderColor = useCallback((growthRate: number): '#00FF00' | '#FF0D19' | '#616161' => {
    if (growthRate > 0) return '#00FF00';
    if (growthRate < 0) return '#FF0D19';
    return '#616161';
  }, []);

  useEffect(() => {
    getOrderAnalytics({
      shop: config.shop,
      startTime: saleReport.startTime,
      endTime: saleReport.endTime,
      previousPeriodStartTime: saleReport.previousPeriodStartTime,
      previousPeriodEndTime: saleReport.previousPeriodEndTime,
      locationIds:
        saleReport.locationSelected[0] === ''
          ? listLocationName.map((item) => Number(item.value))
          : [Number(saleReport.locationSelected[0])],
    });
  }, [
    getOrderAnalytics,
    listLocationName,
    saleReport.endTime,
    saleReport.previousPeriodEndTime,
    saleReport.previousPeriodStartTime,
    saleReport.startTime,
    saleReport.locationSelected,
  ]);

  // console.log({ start: new Date(saleReport.startTime), end: new Date(saleReport.endTime) });

  return (
    <BlockStack gap="400">
      <InlineStack gap="200" align="end">
        <DateRangePicker
          range={{ since: new Date(saleReport.startTime), until: new Date(saleReport.endTime) }}
          datePresets={dateRangesDashboard}
          updateDateRange={(dateRange) => {
            const result = getDateRangeWithPreviousPeriod({
              start: dateRange.since as Date,
              end: dateRange.until as Date,
            });

            setSaleReport((prev) => ({
              ...prev,
              startTime: result.startTime,
              endTime: result.endTime,
              previousPeriodStartTime: result.previousPeriodStartTime,
              previousPeriodEndTime: result.previousPeriodEndTime,
            }));
          }}
          disableDatesAfter={today}
          defaultDatePresetChoice={dateRangesDashboard[2]}
        />

        <Popover active={popOverActive.isLocation} activator={locationActivator} onClose={toggleLocationPopoverActive}>
          <Box padding="200">
            <ChoiceList
              title=""
              choices={locationChoices}
              selected={saleReport.locationSelected}
              onChange={handleChangeLocation}
            />
          </Box>
        </Popover>
      </InlineStack>
      <InlineGrid columns={{ md: 2, xs: 1 }} gap="300">
        <Card padding={'0'}>
          <BlockStack align="center" gap={'100'} inlineAlign="center">
            <Box paddingBlockStart={'300'} paddingInline={'200'} paddingBlockEnd={'200'}>
              <BoldText>{t('sale.saleScheduled')}</BoldText>
            </Box>
            <FullWidthDiv>
              <Divider borderColor="border" />
            </FullWidthDiv>
            <Box padding={'300'} paddingBlockStart={'200'}>
              <BoldText variant="headingXl">
                {`${Number(orderAnalyticData?.data.totalRevenue || 0).toLocaleString('de-DE')} ${crispData?.data.currency || ''}`}
              </BoldText>
            </Box>
          </BlockStack>
        </Card>
        <Card padding={'0'}>
          <BlockStack align="center" gap={'100'} inlineAlign="center">
            <Box paddingBlockStart={'300'} paddingInline={'200'} paddingBlockEnd={'200'}>
              <BoldText>{t('sale.orderScheduled')}</BoldText>
            </Box>
            <FullWidthDiv>
              <Divider borderColor="border" />
            </FullWidthDiv>
            <Box padding={'300'} paddingBlockStart={'200'}>
              <BoldText variant="headingXl">{`${orderAnalyticData?.data.orderCount || 0} orders`}</BoldText>
            </Box>
          </BlockStack>
        </Card>
      </InlineGrid>
      {orderOverViewData.length > 0 && (
        <Card padding={'0'}>
          <Box padding={'400'}>
            <InlineStack gap={'200'}>
              <BoldText>{t('sale.overView')}</BoldText>
              {orderOverViewData.every((item) => !item.isEnabled) && <Badge tone="info">Demo</Badge>}
            </InlineStack>
          </Box>
          <Divider borderColor="border" />
          <Box padding={'400'}>
            <InlineGrid columns={{ md: orderOverViewData.length, xs: 1 }} gap="300">
              {orderOverViewData.map((item) => (
                <Box key={item.title} opacity={item.isEnabled ? '1' : '0.7'}>
                  <Card padding={'0'}>
                    <Box padding={'300'}>
                      <BoldText>{item.title}</BoldText>
                    </Box>
                    <Divider borderColor="border" />
                    <Box padding={'300'}>
                      <BlockStack>
                        <InlineStack gap={lgUp ? '1000' : '600'} blockAlign="baseline">
                          <RegularText>{t('sale.overViewDetail.schedule')}</RegularText>
                          <InlineStack gap={'300'} blockAlign="center">
                            <RegularText variant="headingXl">{item.scheduleOrders}</RegularText>
                            <CustomColorGrowth color={renderColor(item.growth)}>
                              <RegularText variant="bodyXs">{`${item.growth > 0 ? '+' : ''}${item.growth}%`}</RegularText>
                            </CustomColorGrowth>
                          </InlineStack>
                        </InlineStack>
                        <InlineStack blockAlign="center">
                          <PointerDiv
                            isPointer={item.isEnabled}
                            onClick={() => {
                              item.isEnabled && window.open(`https://admin.shopify.com/store/${config.storeName}/orders`);
                            }}
                          >
                            <Box color="text-link">
                              <RegularText>{t('sale.overViewDetail.view')}</RegularText>
                            </Box>
                          </PointerDiv>
                          <PointerDiv
                            isPointer={item.isEnabled}
                            onClick={() =>
                              item.isEnabled && window.open(`https://admin.shopify.com/store/${config.storeName}/orders`)
                            }
                          >
                            <Box paddingInlineStart={'100'}>
                              <Icon source={ArrowRightIcon} />
                            </Box>
                          </PointerDiv>
                        </InlineStack>
                        <Box paddingBlockStart={'100'} paddingBlockEnd={'300'}>
                          <Divider />
                        </Box>
                        <InlineStack gap={lgUp ? '1000' : '600'} blockAlign="baseline">
                          <RegularText>{t('sale.overViewDetail.unfulfilled')}</RegularText>
                          <Box paddingInlineStart={'050'}>
                            <RegularText variant="headingXl">{item.unfulfilledOrders}</RegularText>
                          </Box>
                        </InlineStack>
                        <InlineStack blockAlign="center">
                          <PointerDiv
                            isPointer={item.isEnabled}
                            onClick={() =>
                              item.isEnabled && window.open(`https://admin.shopify.com/store/${config.storeName}/orders`)
                            }
                          >
                            <Box color="text-link">
                              <RegularText>{t('sale.overViewDetail.view')}</RegularText>
                            </Box>
                          </PointerDiv>
                          <PointerDiv
                            isPointer={item.isEnabled}
                            onClick={() =>
                              item.isEnabled && window.open(`https://admin.shopify.com/store/${config.storeName}/orders`)
                            }
                          >
                            <Box paddingInlineStart={'100'}>
                              <Icon source={ArrowRightIcon} />
                            </Box>
                          </PointerDiv>
                        </InlineStack>
                      </BlockStack>
                    </Box>
                  </Card>
                </Box>
              ))}
            </InlineGrid>
          </Box>
        </Card>
      )}
    </BlockStack>
  );
};
